import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const LABEL = { partner_welcome: "Adelaida's partner welcome (spoken)", explain: "What is Xcelio?", share: "Share posts (intent → channel)", hooks: "Video hooks", invite_reasons: "Personal invite (Adelaida's words per reason)",
  gift: "Gift a Reset", daily_posts: "Post today library", dms: "Message templates", objections: "Objection answers", newsletter: "Newsletter copy",
  content_ideas: "Content ideas", plan_weeks: "30-day plan", brand: "Brand guidelines", compliance: "Compliance wording", terms: "Commission terms" };
const set = (obj, path, v) => { if (!path.length) return v; const c = Array.isArray(obj) ? [...obj] : { ...obj }; c[path[0]] = set(obj[path[0]], path.slice(1), v); return c; };

// Every string in the partner copy as [path, text]; keys (structure) stay fixed.
const flat = (v, path = []) => typeof v === "string" ? [[path, v]] : v && typeof v === "object" ? Object.entries(v).flatMap(([k, x]) => flat(x, [...path, k])) : [];

function Fields({ value, root, onChange }) {
  return (
    <div className="space-y-3">
      {flat(value, [root]).map(([path, text]) => (
        <div key={path.join(".")}>
          <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{path.slice(1).map((x) => String(x).replace(/_/g, " ")).join(" · ") || "text"}</p>
          <Textarea value={text} rows={text.length > 90 || text.includes("\n") ? 4 : 1} onChange={(e) => onChange(path, e.target.value)} className="text-sm" data-testid={`copy-${path.join("-")}`} />
        </div>
      ))}
    </div>
  );
}

export default function AdminPartnerCopy() {
  const [copy, setCopy] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/partner-copy").then((r) => setCopy(r.data.copy)).catch(() => {}); }, []);
  if (!copy) return null;
  const onChange = (path, v) => setCopy((c) => set(c, path, v));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/partner-copy", { copy }); setCopy(data.copy); toast.success("Partner copy saved"); }
    catch { toast.error("Couldn't save"); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Put all partner copy back to Xcelio's original wording?")) return;
    const { data } = await api.delete("/admin/partner-copy"); setCopy(data.copy); toast("Back to the original wording");
  };
  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="admin-partner-copy">
      <h2 className="font-serif text-3xl mb-2">Partner copy</h2>
      <p className="text-muted-foreground mb-6 max-w-xl text-sm">Everything partners see and share. Keep {"{name}"}, {"{link}"}, {"{code}"} and {"{friend}"} where they are; Xcelio fills them in for each partner.</p>
      <Accordion type="multiple" className="rounded-2xl border border-border px-4 mb-4">
        {Object.keys(copy).map((k) => (
          <AccordionItem key={k} value={k} className="border-border">
            <AccordionTrigger className="text-sm" data-testid={`admin-copy-${k}`}>{LABEL[k] || k}</AccordionTrigger>
            <AccordionContent><Fields value={copy[k]} root={k} onChange={onChange} /></AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="flex gap-2">
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="admin-copy-save">Save partner copy</Button>
        <Button onClick={reset} variant="outline" className="rounded-full" data-testid="admin-copy-reset">Restore original</Button>
      </div>
    </div>
  );
}
